import React from 'react';
import { computeMenuOptions, openPbxDialer, triggerMenuOption } from 'modules/contacts/stores/contacts-slice-handler';
import { ContactMenuOption, ContactMenuType, UserType } from '../../../constants/contacts-constants';
import ContextMenu from '../../shared/components/context-menu';
import { getAllContactsMenusOptionsOrdered, computeUserName } from '../../../utils/contact-util';
import t from 'utils/translate';
import Tooltip from '@mui/material/Tooltip';
import { randUuid } from '../../../utils/functions';
import { useSelector } from 'react-redux';
import { openGuestInvitationModal } from '../stores/guest-invitation-slice-handler';
import { getCompanySettings } from '../../settings/stores/company-settings-slice-handler';

/**
 * @param {CugContact|null} contact The contact for which the menu must be attached. It can be NULL (case of Contacts Page)
 * @param {ContactMenuType} menuType
 * @param {Function|null} optionClick The function to be called when the option is clicked. (optional)
 * @param {boolean} linear Render the menu options on a single row rather than a context menu. (optional, default false)
 * @param {ContactMenuOption[]} ignoredOptions A list of options that should not be displayed regardless if are computed by store.
 * @return {JSX.Element}
 * @constructor
 */
export default function ContactMenu({
    contact = null,
    menuType = ContactMenuType.CONTACTS_PAGE,
    optionClick = null,
    linear = false,
    ignoredOptions = [],
}) {
    const menuOptions = useSelector(() => computeMenuOptions(contact, menuType));

    const menuItems = getAllContactsMenusOptionsOrdered(contact).filter(
        // check for full name (surrounded with ')
        (menuItem) => menuOptions.includes(`,${menuItem.feature},`) && !ignoredOptions.includes(menuItem.feature),
    );

    /**
     * @type {ContactMenuOption[]}
     */
    const featuresWithAvailabilityRestrictions = [
        ContactMenuOption.PTT_CALL,
        ContactMenuOption.CONFERENCE_CALL,
        ContactMenuOption.BROADCAST_CALL
    ];

    const triggerSubMenuItem = (menuOption, availableOnlyReception) => {
        triggerMenuOption(contact, menuOption, { availableOnlyReception });
    };

    // add the standard callback to all items
    menuItems.map((menuItem) => {
        // must add the availability restrictions (reach only on duty)
        if (
            contact !== null &&
            contact.IsGroup &&
            contact.AvailableOnlyReception === true &&
            featuresWithAvailabilityRestrictions.includes(menuItem.feature)
        ) {
            menuItem.subMenu = {
                className: 'available-only-reception-menu',
                items: [
                    {
                        glyphFontIcon: 'stw-circle',
                        text: t('js_all_popup_walkie_talkie_available_short'),
                        callback: () => triggerSubMenuItem(menuItem.feature, true),
                    },
                    {
                        glyphFontIcon: 'stw-circle',
                        text: t('js_all_popup_walkie_talkie_short'),
                        callback: () => triggerSubMenuItem(menuItem.feature, false),
                    },
                ],
            };

            // should  not do anything on click; only hover will show the sub-menu
            menuItem.callback = () => false;

            return;
        }

        menuItem.callback = () => {
            if (optionClick !== null) {
                optionClick(contact, menuItem.feature);
            } else {
                // execute default menu action over a contact
                triggerMenuOption(contact, menuItem.feature);
            }
        };
    });

    if (linear === true) {
        return (
            <div className="contact-menu-linear">
                {menuItems.map((menuItem) => (
                    <div
                        data-role={menuItem.feature}
                        key={`linear-menu-option-${randUuid()}`}
                        data-test-id={`contacts-menu-item-${menuItem.feature}`}
                    >
                        <Tooltip title={menuItem.text} arrow>
                            <div className={'menu-item'} onClick={() => menuItem.callback()}>
                                <span className={`item-icon ${menuItem.glyphFontIcon}`}></span>
                            </div>
                        </Tooltip>
                    </div>
                ))}
            </div>
        );
    }

    let dataTestId = `contacts-context-menu-main`;
    if (contact !== null) {
        switch (contact._type) {
            case UserType.GROUP:
                dataTestId = `contacts-context-menu-${contact._name}`;
                break;

            case UserType.USER:
            case UserType.CARTRACKER:
            case UserType.THREE_GPP:
            case UserType.ROBOT:
                dataTestId = `contacts-context-menu-${contact.msisdn}`;
                break;

            case UserType.EXTERNAL_VIDEO_DESTINATION:
            case UserType.EXTERNAL_VIDEO_SOURCE:
                dataTestId = `contacts-context-menu-${computeUserName(contact)}`;
                break;

            default:
        }
    }

    return (
        <ContextMenu
            trigger={
                <div className={'menu-container'} data-test-id={dataTestId}>
                    <span className={'stw-three-dots-menu'}></span>
                </div>
            }
            position={'right'}
            items={menuItems}
        />
    );
}
